import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Button,
  TextField,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Send as SendIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import { apiService } from '@/services/api';

interface Communication {
  id: string;
  communication_type: 'sms' | 'email';
  subject?: string;
  message: string;
  total_recipients: number;
  successful_count: number;
  failed_count: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  created_at: string;
  admin_name: string;
}

interface Recipient {
  id: string;
  recipient_contact: string;
  recipient_name: string;
  recipient_role: string;
  status: 'pending' | 'sent' | 'delivered' | 'failed';
  error_message?: string;
  sent_at?: string;
}

interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  role: string;
}

const Communication: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [smsMessage, setSmsMessage] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [communications, setCommunications] = useState<Communication[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [selectedComm, setSelectedComm] = useState<Communication | null>(null);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [recipientCount, setRecipientCount] = useState(0);
  const [userSelectionDialog, setUserSelectionDialog] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const roles = [
    { value: 'farmer', label: 'Farmers' },
    { value: 'buyer', label: 'Buyers' },
    { value: 'advisor', label: 'Advisors' },
    { value: 'admin', label: 'Admins' }
  ];

  useEffect(() => {
    loadCommunications();
  }, [currentTab]);

  useEffect(() => {
    if (selectedRoles.length > 0 || selectedUserIds.length > 0) {
      fetchRecipientCount();
    } else {
      setRecipientCount(0);
    }
  }, [selectedRoles, selectedUserIds]);

  const fetchRecipientCount = async () => {
    try {
      const response = await apiService.getRecipientCount(selectedRoles, selectedUserIds);
      setRecipientCount((response.data as any).count);
    } catch (err) {
      console.error('Error fetching recipient count:', err);
    }
  };

  const fetchUsersForRoles = async () => {
    if (selectedRoles.length === 0) {
      setError('Please select at least one role first');
      return;
    }

    setLoadingUsers(true);
    try {
      const response = await apiService.getUsersByRoles(selectedRoles);
      setAvailableUsers((response.data as any).users || []);
      setUserSelectionDialog(true);
    } catch (err: any) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleUserToggle = (userId: string) => {
    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAllUsers = () => {
    if (selectedUserIds.length === availableUsers.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(availableUsers.map(u => u.id));
    }
  };

  const handleCloseUserSelection = () => {
    setUserSelectionDialog(false);
  };

  const loadCommunications = async () => {
    setLoading(true);
    setError('');
    try {
      const type = currentTab === 0 ? 'sms' : 'email';
      const response = await apiService.getCommunicationHistory(type);
      setCommunications(response.data as Communication[]);
    } catch (err: any) {
      setError(err.message || 'Failed to load communications');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleToggle = (role: string) => {
    setSelectedRoles(prev =>
      prev.includes(role)
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  const handleSendSMS = async () => {
    if (!smsMessage.trim()) {
      setError('Please enter a message');
      return;
    }

    if (selectedRoles.length === 0 && selectedUserIds.length === 0) {
      setError('Please select at least one recipient role or specific users');
      return;
    }

    setSending(true);
    setError('');
    setSuccess('');

    try {
      await apiService.sendBulkSMS({
        message: smsMessage,
        recipient_roles: selectedRoles.length > 0 ? selectedRoles : undefined,
        recipient_ids: selectedUserIds.length > 0 ? selectedUserIds : undefined
      });

      setSuccess(`SMS sent successfully to ${recipientCount} recipients`);
      setSmsMessage('');
      setSelectedRoles([]);
      setSelectedUserIds([]);
      loadCommunications();
    } catch (err: any) {
      setError(err.message || 'Failed to send SMS');
    } finally {
      setSending(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailSubject.trim()) {
      setError('Please enter a subject');
      return;
    }

    if (!emailMessage.trim()) {
      setError('Please enter a message');
      return;
    }

    if (selectedRoles.length === 0 && selectedUserIds.length === 0) {
      setError('Please select at least one recipient role or specific users');
      return;
    }

    setSending(true);
    setError('');
    setSuccess('');

    try {
      await apiService.sendBulkEmail({
        subject: emailSubject,
        message: emailMessage,
        recipient_roles: selectedRoles.length > 0 ? selectedRoles : undefined,
        recipient_ids: selectedUserIds.length > 0 ? selectedUserIds : undefined
      });

      setSuccess(`Email sent successfully to ${recipientCount} recipients`);
      setEmailSubject('');
      setEmailMessage('');
      setSelectedRoles([]);
      setSelectedUserIds([]);
      loadCommunications();
    } catch (err: any) {
      setError(err.message || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const handleViewDetails = async (comm: Communication) => {
    setSelectedComm(comm);
    setDetailsDialog(true);
    
    try {
      const response = await apiService.getCommunicationDetails(comm.id);
      setRecipients((response.data as any).recipients);
    } catch (err) {
      console.error('Error loading recipients:', err);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'processing': return 'info';
      case 'failed': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getRecipientStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
      case 'delivered': return 'success';
      case 'failed': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Bulk Communication
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Send SMS and emails to farmers, buyers, advisors, and admins
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)}>
          <Tab label="SMS" />
          <Tab label="Email" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          {/* Recipient Selection */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Select Recipients
              </Typography>
              <FormGroup row>
                {roles.map(role => (
                  <FormControlLabel
                    key={role.value}
                    control={
                      <Checkbox
                        checked={selectedRoles.includes(role.value)}
                        onChange={() => handleRoleToggle(role.value)}
                      />
                    }
                    label={role.label}
                  />
                ))}
              </FormGroup>
              <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={loadingUsers ? <CircularProgress size={20} /> : <PeopleIcon />}
                  onClick={fetchUsersForRoles}
                  disabled={loadingUsers || selectedRoles.length === 0}
                >
                  Select Specific Users
                </Button>
                {selectedUserIds.length > 0 && (
                  <Chip
                    label={`${selectedUserIds.length} user${selectedUserIds.length !== 1 ? 's' : ''} selected`}
                    onDelete={() => setSelectedUserIds([])}
                    color="primary"
                  />
                )}
              </Box>
              {recipientCount > 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {recipientCount} recipient{recipientCount !== 1 ? 's' : ''} will receive this message
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* SMS Tab */}
          {currentTab === 0 && (
            <Box>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="SMS Message"
                value={smsMessage}
                onChange={(e) => setSmsMessage(e.target.value)}
                placeholder="Enter your SMS message here..."
                helperText={`${smsMessage.length} characters`}
                sx={{ mb: 2 }}
              />
              <Button
                variant="contained"
                startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
                onClick={handleSendSMS}
                disabled={sending || !smsMessage.trim() || (selectedRoles.length === 0 && selectedUserIds.length === 0)}
              >
                {sending ? 'Sending...' : 'Send SMS'}
              </Button>
            </Box>
          )}

          {/* Email Tab */}
          {currentTab === 1 && (
            <Box>
              <TextField
                fullWidth
                label="Subject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Enter email subject..."
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                rows={8}
                label="Email Message"
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                placeholder="Enter your email message here..."
                sx={{ mb: 2 }}
              />
              <Button
                variant="contained"
                startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
                onClick={handleSendEmail}
                disabled={sending || !emailSubject.trim() || !emailMessage.trim() || (selectedRoles.length === 0 && selectedUserIds.length === 0)}
              >
                {sending ? 'Sending...' : 'Send Email'}
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Communication History */}
      <Paper>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {currentTab === 0 ? 'SMS' : 'Email'} History
          </Typography>
          <IconButton onClick={loadCommunications} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Box>

        {loading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  {currentTab === 1 && <TableCell>Subject</TableCell>}
                  <TableCell>Message</TableCell>
                  <TableCell>Recipients</TableCell>
                  <TableCell>Success Rate</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {communications.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={currentTab === 1 ? 7 : 6} align="center">
                      No {currentTab === 0 ? 'SMS' : 'email'} communications found
                    </TableCell>
                  </TableRow>
                ) : (
                  communications.map((comm) => (
                    <TableRow key={comm.id}>
                      <TableCell>{formatDateTime(comm.created_at)}</TableCell>
                      {currentTab === 1 && <TableCell>{comm.subject}</TableCell>}
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {comm.message}
                        </Typography>
                      </TableCell>
                      <TableCell>{comm.total_recipients}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" color="success.main">
                            ✓ {comm.successful_count}
                          </Typography>
                          {comm.failed_count > 0 && (
                            <Typography variant="body2" color="error.main">
                              ✗ {comm.failed_count}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={comm.status}
                          color={getStatusColor(comm.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(comm)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialog}
        onClose={() => setDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Communication Details
        </DialogTitle>
        <DialogContent>
          {selectedComm && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Type
                  </Typography>
                  <Typography variant="body1">
                    {selectedComm.communication_type.toUpperCase()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={selectedComm.status}
                    color={getStatusColor(selectedComm.status) as any}
                    size="small"
                  />
                </Grid>
                {selectedComm.subject && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Subject
                    </Typography>
                    <Typography variant="body1">
                      {selectedComm.subject}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Message
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedComm.message}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Sent by
                  </Typography>
                  <Typography variant="body1">
                    {selectedComm.admin_name}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Date
                  </Typography>
                  <Typography variant="body1">
                    {formatDateTime(selectedComm.created_at)}
                  </Typography>
                </Grid>
              </Grid>

              <Typography variant="h6" gutterBottom>
                Recipients ({recipients.length})
              </Typography>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Contact</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Sent At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recipients.map((recipient) => (
                      <TableRow key={recipient.id}>
                        <TableCell>{recipient.recipient_name}</TableCell>
                        <TableCell>{recipient.recipient_contact}</TableCell>
                        <TableCell>
                          <Chip label={recipient.recipient_role} size="small" />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={recipient.status}
                            color={getRecipientStatusColor(recipient.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {recipient.sent_at ? formatDateTime(recipient.sent_at) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* User Selection Dialog */}
      <Dialog
        open={userSelectionDialog}
        onClose={handleCloseUserSelection}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Select Specific Users
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              onClick={handleSelectAllUsers}
              fullWidth
            >
              {selectedUserIds.length === availableUsers.length ? 'Deselect All' : 'Select All'}
            </Button>
          </Box>
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedUserIds.length === availableUsers.length && availableUsers.length > 0}
                      indeterminate={selectedUserIds.length > 0 && selectedUserIds.length < availableUsers.length}
                      onChange={handleSelectAllUsers}
                    />
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Role</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {availableUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No users found for selected roles
                    </TableCell>
                  </TableRow>
                ) : (
                  availableUsers.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedUserIds.includes(user.id)}
                          onChange={() => handleUserToggle(user.id)}
                        />
                      </TableCell>
                      <TableCell>{user.first_name} {user.last_name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.phone}</TableCell>
                      <TableCell>
                        <Chip label={user.role} size="small" />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info">
              {selectedUserIds.length} user{selectedUserIds.length !== 1 ? 's' : ''} selected
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUserSelection}>Cancel</Button>
          <Button onClick={handleCloseUserSelection} variant="contained">
            Confirm Selection
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Communication;
